import React from "react";
import { MapPin, AlertCircle } from "lucide-react";
import { loadGoogleMaps } from "@/lib/googleMaps";

/**
 * Drop-in <input> with Google Places Autocomplete attached.
 * Bias to UK addresses. Falls back to a plain text input if the JS API
 * fails to load (e.g. API key missing / Places API not enabled).
 *
 * Props:
 *   value, onChange(value:string)
 *   onPlaceSelected(place) — fires when user picks a result
 *   placeholder, required, testId, className
 */
export default function PlaceAutocompleteInput({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  required,
  testId,
  className,
}) {
  const inputRef = React.useRef(null);
  const acRef = React.useRef(null);
  const [loadError, setLoadError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current || !google?.maps?.places) return;
        const ac = new google.maps.places.Autocomplete(inputRef.current, {
          types: ["geocode"],
          componentRestrictions: { country: ["gb"] },
          fields: ["formatted_address", "geometry", "name", "place_id"],
        });
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          const addr = place?.formatted_address || place?.name || "";
          if (addr) onChange(addr);
          if (onPlaceSelected) onPlaceSelected(place);
        });
        acRef.current = ac;
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || "Google Maps unavailable");
      });
    return () => {
      cancelled = true;
      if (acRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(acRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1E4D55] pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className={`w-full pl-9 pr-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm ${className || ""}`}
        autoComplete="off"
      />
      {loadError && (
        <p className="mt-1 text-[11px] text-[#A66800] inline-flex items-center gap-1">
          <AlertCircle size={11} /> {loadError} — type the address manually.
        </p>
      )}
    </div>
  );
}
